import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { Dialog, Slider, styled, Grid } from "@mui/material";
import { useState, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";
import { PLACEHOLDER_GREEN_IMAGE } from "../../utils/constants/Constants.js";
import { Button } from "../Button/Button.js";
const cropWindow = {
  width: 400,
  height: 360
};
const CropperContainer = styled("div")({
  width: cropWindow.width,
  padding: 25,
  ".crop": {
    position: "relative",
    width: "100%",
    height: cropWindow.height,
    background: "#fff",
    borderRadius: 10,
    overflow: "hidden"
  },
  ".control": {
    padding: "15px 0 0 10px",
    display: "flex",
    alignItems: "center"
  }
});
const LoadingContainer = styled(Grid, {
  shouldForwardProp: (prop) => !["aspectRatio"].includes(prop.toString())
})(({ aspectRatio }) => ({
  width: cropWindow.width,
  height: cropWindow.height,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  img: {
    aspectRatio: `${aspectRatio}`,
    overflow: "hidden"
  }
}));
const ImageCropper = ({
  open,
  setOpen,
  file,
  fileTypeToUse,
  cropperProps,
  onFileChange
}) => {
  const [imageSrc, setImageSrc] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [zoom, setZoom] = useState(1);
  const onCropComplete = useCallback((_croppedArea, croppedAreaPixels2) => {
    setCroppedAreaPixels(croppedAreaPixels2);
  }, []);
  const cropIt = useCallback(async () => {
    var _a;
    if (file) {
      try {
        const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, fileTypeToUse || (file == null ? void 0 : file.type));
        const newFileEnding = (_a = fileTypeToUse == null ? void 0 : fileTypeToUse.split("/")) == null ? void 0 : _a[1];
        const newFileName = newFileEnding ? `${file.name.substring(0, file.name.lastIndexOf("."))}.${newFileEnding}` : file.name;
        const newFile = new File([croppedImage], newFileName, {
          type: fileTypeToUse || (file == null ? void 0 : file.type)
        });
        onFileChange(newFile);
        setOpen(false);
      } catch (e) {
        console.error("error cropping image: ", e);
      }
    }
  }, [imageSrc, croppedAreaPixels, file, onFileChange, setOpen]);
  useEffect(() => {
    setCrop({ x: 0, y: 0 });
  }, [imageSrc]);
  useEffect(() => {
    const initImage = async () => {
      const imageDataUrl = await readFile(file);
      setImageSrc(imageDataUrl);
    };
    if (file) {
      initImage();
    }
  }, [file]);
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      open,
      onClose: () => {
        setOpen(false);
      },
      maxWidth: "lg",
      children: /* @__PURE__ */ jsx(CropperContainer, { children: imageSrc ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: "crop",
            onKeyDown: (e) => {
              console.log(e);
            },
            children: /* @__PURE__ */ jsx(
              Cropper,
              {
                image: imageSrc,
                crop,
                zoom,
                onCropChange: setCrop,
                onZoomChange: setZoom,
                onCropComplete,
                restrictPosition: false,
                showGrid: false,
                ...cropperProps,
                minZoom: 0.5
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs("div", { className: "control", children: [
          /* @__PURE__ */ jsx("div", { style: { flex: 1, marginRight: 30 }, children: /* @__PURE__ */ jsx(
            Slider,
            {
              value: zoom,
              sx: (theme) => ({
                color: theme.vars.palette.text.primary
              }),
              min: 0,
              max: 3,
              step: 0.1,
              "aria-labelledby": "Zoom",
              classes: {},
              onChange: (e, zoom2) => setZoom(zoom2)
            }
          ) }),
          /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Button, { onClick: cropIt, variant: "solid", color: "primary", children: "Crop" }) })
        ] })
      ] }) : /* @__PURE__ */ jsx(LoadingContainer, { aspectRatio: cropperProps.aspect, children: /* @__PURE__ */ jsx("img", { src: PLACEHOLDER_GREEN_IMAGE["1X1"], alt: "loading-placeholder" }) }) })
    }
  );
};
const readFile = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
};
const createImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.addEventListener("load", () => resolve(image));
  image.addEventListener("error", (error) => reject(error));
  image.setAttribute("crossOrigin", "anonymous");
  image.src = url;
});
const getCroppedImg = async (imageSrc, pixelCrop, type) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return null;
  }
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.fillStyle = "#ffffff00";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );
  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve(file);
    }, type);
  });
};
export {
  ImageCropper
};
//# sourceMappingURL=ImageCropper.js.map
